import React, { useEffect, useRef, useState } from "react";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { StyledDivider } from "../../HeaderStyles";
import AccountsMenu from "../../AccountsMenu";
import { capitalize } from "@utils";
import UserMenu from "../../UserMenu";
import useAppStore from "src/stores/appStore";
import {
  infoPopoversSelector,
  setInfoPopoversSelector
} from "src/stores/selectors/tripletsSelectors";
import {
  collectionConfigChangedSelector,
  isSavingChangesSelector,
  selectedCollectionSelector,
  setCollectionConfigChangedSelector,
  setDialogErrorSelector,
  setSettingsConfigChangedSelector,
  settingsConfigChangedSelector,
  updateChannelSettingsSelector,
  updateCollectionSelector,
  validationResultsSelector,
  setSettingsPhoneNumbersChangedSelector } from "src/stores/selectors/collectionsSelectors";
import {
  policiesConfigChangedSelector,
  setPoliciesConfigChangedSelector,
  policyConfigChangedSelector,
  setPolicyConfigChangedSelector
} from "src/stores/selectors/policiesSelectors";
import {
  pollPreviewStatusSelector
} from "src/stores/selectors/previewSelectors";
import ArrowLeftIcon from "src/icons/ArrowLeftIcon";
import {
  assistantIdSelector,
  setIsAssistantPreviewableSelector,
  setSystemModeSelector
} from "src/stores/selectors/appSelectors";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import useDraftPublishHeader from "../status-header/DraftPublishing/useDraftPublishHeader";
import Dialog from "@hyro-components/Dialogs/HyroUiDialog";
import {
  DONT_SHOW_AGAIN_STORAGE,
  POPOVERS,
  REFRESH_PAGE
} from "../status-header/DraftPublishing/DraftHeaderConsts";
import { DIALOG_VARIANTS } from "@hyro-components/Dialogs/DialogConsts";
import InfoPopover from "../status-header/DraftPublishing/InfoPopover";
import { EXIT_EDIT_MODE_DIALOG } from "../../../consts";
import { getValidationsByType } from "src/components/collections/helpers";
import { VALIDATION_TYPES } from "src/components/collections/consts";
import { useMutateCollection } from "src/queries/hooks/collections";
import { useDraft } from "src/queries/hooks/assistants";
import PreviewManager from "../../preview/PreviewManager";
import DevBanner from "src/layout/Header/header-types/dev-banner/DevBanner";
import ActionButton from "./action-button/ActionButton";
import NotificationContainer from "src/components/widgets/notifications/notifications-container/NotificationsContainer";
import { useFlags } from "launchdarkly-react-client-sdk";
import useBuildStatusSocket from "src/layout/Header/preview/useBuildStatusSocket";

const EditModeHeader = ({ selectedAssistant }) => {
  const theme = useTheme();
  const setIsAssistantPreviewable = useAppStore(setIsAssistantPreviewableSelector);
  const actionButtonRef = useRef();
  const notificationButtonRef = useRef();
  const setSystemMode = useAppStore(setSystemModeSelector);
  const selectedCollection = useAppStore(selectedCollectionSelector);
  const updateCollection = useAppStore(updateCollectionSelector);
  const updateSettings = useAppStore(updateChannelSettingsSelector);
  const setCollectionConfigChanged = useAppStore(setCollectionConfigChangedSelector);
  const setSettingsConfigChanged = useAppStore(setSettingsConfigChangedSelector);
  const setPoliciesConfigChanged = useAppStore(setPoliciesConfigChangedSelector);
  const isSavingChanges = useAppStore(isSavingChangesSelector);
  const collectionConfigChanged = useAppStore(collectionConfigChangedSelector);
  const settingsConfigChanged = useAppStore(settingsConfigChangedSelector);
  const policiesConfigChanged = useAppStore(policiesConfigChangedSelector);
  const policyConfigChanged = useAppStore(policyConfigChangedSelector);
  const setPolicyConfigChanged = useAppStore(setPolicyConfigChangedSelector);
  const { data: draft } = useDraft();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const selectedAssistantId = useAppStore(assistantIdSelector);
  const [dontShowPopovers, setDontShowPopovers] = useState(() => ({
    draftExpiration: localStorage.getItem(DONT_SHOW_AGAIN_STORAGE.DRAFT_EXPIRATION),
    buildDraft: localStorage.getItem(DONT_SHOW_AGAIN_STORAGE.BUILD_DRAFT),
    setLive: localStorage.getItem(DONT_SHOW_AGAIN_STORAGE.SET_LIVE)
  }));
  const infoPopovers = useAppStore(infoPopoversSelector);
  const setInfoPopovers = useAppStore(setInfoPopoversSelector);
  const pollBuildStatus = useAppStore(pollPreviewStatusSelector);
  const getValidationResults = useAppStore(state => state.getValidationResults);
  const setDialogError = useAppStore(setDialogErrorSelector);
  const validationResults = useAppStore(validationResultsSelector);
  const unassigned = getValidationsByType(validationResults, VALIDATION_TYPES.UNASSIGNED);
  const updateCollectionMutation = useMutateCollection();
  const setSettingsPhoneNumbersChanged = useAppStore(setSettingsPhoneNumbersChangedSelector);
  const { enableBuildStatusSocket } = useFlags();

  useBuildStatusSocket(enableBuildStatusSocket && !!selectedAssistantId && !!draft);

  useEffect(() => {
    setIsAssistantPreviewable();

    if (selectedAssistantId && draft) {
      try {
        getValidationResults();
        if (!enableBuildStatusSocket) {
          pollBuildStatus();
        }
      } catch (err) {
        console.error(err);
        setDialogError(true);
      }
    }

  }, [selectedAssistantId, draft, pollBuildStatus, setIsAssistantPreviewable, getValidationResults, setDialogError, enableBuildStatusSocket]);

  const handleExitClick = () => {
    if (collectionConfigChanged || settingsConfigChanged || policiesConfigChanged || policyConfigChanged) {
      setIsExitDialogOpen(true);
    } else {
      setSystemMode(SYSTEM_MODES.READ_ONLY);
    }
  };

  const handleDiscard = () => {
    setSettingsPhoneNumbersChanged(false);
    setPoliciesConfigChanged(false);
    setPolicyConfigChanged(false);
    setIsExitDialogOpen(false);
    setSystemMode(SYSTEM_MODES.READ_ONLY);
  };

  const handleSubmit = async () => {
    if (collectionConfigChanged || policiesConfigChanged) {
      await updateCollection(selectedCollection, updateCollectionMutation);
      setCollectionConfigChanged(false);
      setPoliciesConfigChanged(false);
      setPolicyConfigChanged(false);
    } else {
      await updateSettings();
      setSettingsConfigChanged(false);
      setSettingsPhoneNumbersChanged(false);
    }

    setIsExitDialogOpen(false);
    setSystemMode(SYSTEM_MODES.READ_ONLY);
  };

  const handleGotIt = (type, checked) => {
    if (checked) {
      localStorage.setItem(type, "true");
      setDontShowPopovers(prev => ({ ...prev, [type]: "true" }));
    }
    setInfoPopovers(type, false);
  };

  const draftErrorsDialog = useDraftPublishHeader();

  return <>
    <Stack direction='row'>
      <Stack sx={{ width: 216 }} direction='row'>
        <Button
          data-testid="exit-edit-mode-button"
          onClick={handleExitClick}
          startIcon={<ArrowLeftIcon />}
          sx={{ ml: "-12px", px: "12px", lineHeight: "20px" }}>
          Exit edit mode
        </Button>
        <StyledDivider sx={{ ml: "auto" }} orientation="vertical" flexItem />
      </Stack>

      <AccountsMenu />

      <DevBanner />

      <Stack direction='row' sx={{ ml: "auto" }} alignItems={"center"} justifyContent='center' gap={1}>
        {selectedAssistant ? <>
          <PreviewManager />
          <div ref={actionButtonRef}>
            <ActionButton />
          </div>
          <NotificationContainer ref={notificationButtonRef} />
        </> : null}
        <UserMenu />
      </Stack>
    </Stack>

    {draftErrorsDialog.open ? <Dialog
      open={draftErrorsDialog.open}
      title={draftErrorsDialog.title}
      submitButtonText={REFRESH_PAGE}
      handleSubmit={() => location.reload()}
      handleCancel={draftErrorsDialog.handleCancel}
      variant={DIALOG_VARIANTS.transactional}
    >
      <Typography>{draftErrorsDialog.text}</Typography>
    </Dialog> : null}
    <InfoPopover
      open={infoPopovers.buildDraft && !dontShowPopovers.buildDraft}
      data={POPOVERS.BUILD_DRAFT}
      anchorRef={actionButtonRef}
      onGotIt={(checked) => handleGotIt(DONT_SHOW_AGAIN_STORAGE.BUILD_DRAFT, checked)} />
    <InfoPopover
      open={infoPopovers.unassigned}
      data={POPOVERS.UNASSIGNED}
      enableCheckbox={false}
      slots={{
        body: <div>
          <Typography variant='body2'>{POPOVERS.UNASSIGNED.body}</Typography>
          <ul style={{ margin: "8px 0", padding: "0 16px" }}>
            {unassigned?.map(({ fieldType, amount }) => <li style={{ ...theme.typography.body2 }} key={fieldType}>
              {amount} {capitalize(amount > 1 ? fieldType : fieldType.slice(0, -1))}
            </li>)}
          </ul>
        </div>
      }}
      anchorRef={actionButtonRef}
      onGotIt={() => setInfoPopovers(DONT_SHOW_AGAIN_STORAGE.UNASSIGNED, false)} />
    <InfoPopover
      open={infoPopovers.setLive && !dontShowPopovers.setLive}
      data={POPOVERS.SET_LIVE}
      anchorRef={notificationButtonRef}
      onGotIt={(checked) => handleGotIt(DONT_SHOW_AGAIN_STORAGE.SET_LIVE, checked)}
    />
    <Dialog
      handleClose={() => setIsExitDialogOpen(false)}
      handleCancel={handleDiscard}
      handleSubmit={handleSubmit}
      open={isExitDialogOpen}
      cancelButtonText={EXIT_EDIT_MODE_DIALOG.CANCEL_BUTTON_TEXT}
      submitButtonText={EXIT_EDIT_MODE_DIALOG.SUBMIT_BUTTON_TEXT}
      submitButtonLoading={isSavingChanges}
      title={EXIT_EDIT_MODE_DIALOG.TITLE}
      variant={DIALOG_VARIANTS.transactional}
      showCloseButton
    >
      <Typography>
        {EXIT_EDIT_MODE_DIALOG.TEXT}
      </Typography>
    </Dialog>
  </>;
};

export default EditModeHeader;
